import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { WatchEvent } from '../../types';
import AlertBox from './AlertBox';
import EventDetails from './EventDetails';
import PageChanges from './PageChanges';
import SearchChanges from './SearchChanges';
import { isRankEvent } from './searchRows';
import { changeSummary } from './timelineFold';
import { tierTone } from './tierTone';
import { partialWarning, sinceLine, when } from './watchEventLines';

/** Alert kinds that are lists of pages: the check's page list says them better. */
const PAGE_KINDS = new Set(['new_section_launched', 'new_pages_published', 'pages_removed']);

/**
 * One check on the Watch feed: what it found, said once.
 *
 * The feed showed a card per alert, so one check that raised eighteen showed
 * eighteen cards. This is the check: its loudest alert as the headline, "18
 * changes" with what they were, since when and from what, and - opened - the
 * pages it added and removed, as on the company's timeline. The alert boxes are
 * a click further.
 */
export default function CheckCard({
  runId,
  events,
  onDismiss,
}: {
  runId: number | null;
  /** Loudest first. */
  events: WatchEvent[];
  /** Dismiss every alert this check raised. */
  onDismiss: (ids: number[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [boxes, setBoxes] = useState(false);
  const lead = events[0];
  const tone = tierTone(lead.tier);
  const n = events.length;
  const unseen = events.some(e => !e.seen_at);
  const pages = runId !== null && events.some(e => PAGE_KINDS.has(e.kind));
  // A rank check opens onto its searches, a row each.
  const searches = runId !== null && !pages && events.some(isRankEvent);
  const since = sinceLine(lead);
  // To this check on the company's timeline, which picks it out.
  const timelineHref = `/watch/subjects/${lead.subject_id}${runId !== null ? `?run=${runId}` : ''}`;
  return (
    <li
      className={`rounded border p-3 ${
        unseen
          ? 'border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-800/50'
          : 'border-gray-200 dark:border-gray-700'
      }`}
      data-testid={`watch-check-${runId ?? lead.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${tone.chip}`} title={tone.hint}>
              {tone.label}
            </span>
            <Link
              to={`/watch/subjects/${lead.subject_id}`}
              className="text-xs text-gray-500 underline dark:text-gray-400"
            >
              {lead.subject_name}
            </Link>
            <span className="text-[11px] text-gray-400">{lead.family_title}</span>
            {lead.detected_at && <span className="text-[11px] text-gray-400">{when(lead.detected_at)}</span>}
          </div>
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            className="block w-full text-left"
          >
            <p className="mt-1 text-sm font-medium">{lead.headline}</p>
            {n === 1 && lead.so_what && (
              <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">{lead.so_what}</p>
            )}
            {since && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{since}</p>}
            {partialWarning(lead) && (
              <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300" data-testid="watch-check-partial">
                {partialWarning(lead)}
              </p>
            )}
            <span className="mt-1 inline-block text-[11px] text-indigo-600 dark:text-indigo-300">
              {open ? 'Hide ▾' : n > 1 ? `${n} changes ▸` : 'Details ▸'}
              {!open && n > 1 && <span className="text-gray-500 dark:text-gray-400"> {changeSummary(events)}</span>}
            </span>
          </button>
          {open && pages && !boxes && (
            <>
              <PageChanges runId={runId!} events={events} />
              <button
                type="button"
                onClick={() => setBoxes(true)}
                className="mt-1 mr-3 text-xs text-gray-500 hover:underline dark:text-gray-400"
              >
                Show the alerts
              </button>
            </>
          )}
          {open && searches && <SearchChanges runId={runId!} events={events} />}
          {open && !pages && !searches && n === 1 && <EventDetails event={lead} />}
          {open && (boxes || (!pages && !searches && n > 1)) && (
            <ul className="mt-2 flex flex-col gap-1.5">
              {events.map(e => (
                <AlertBox key={e.id} event={e} tier={false} />
              ))}
            </ul>
          )}
          {open && (
            <Link
              to={timelineHref}
              className="mt-1 inline-block text-xs text-indigo-600 hover:underline dark:text-indigo-300"
            >
              {lead.subject_name}'s timeline →
            </Link>
          )}
        </div>
        <button
          type="button"
          onClick={() => onDismiss(events.map(e => e.id))}
          className="shrink-0 text-[11px] text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          title={
            n > 1
              ? `Not interested in these ${n} - they also stay out of the digest`
              : 'Not interested in this one - it also stays out of the digest'
          }
        >
          Dismiss
        </button>
      </div>
    </li>
  );
}
